import React from 'react';
import { Link } from 'react-router-dom';
import { TrophyIcon } from '@heroicons/react/24/outline';
import { PlayerPlayMilestonePayload } from '../../../generated-api';

interface PlayerPlayMilestoneCardProps {
  payload: PlayerPlayMilestonePayload;
  createdAt: string;
}

export const PlayerPlayMilestoneCard: React.FC<PlayerPlayMilestoneCardProps> = ({ payload, createdAt }) => {
  const formatRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getMilestoneEmoji = (milestone: number) => {
    switch (milestone) {
      case 5: return '5';
      case 10: return '10';
      case 20: return '20';
      case 50: return '50';
      case 100: return '100';
      default: return milestone.toString();
    }
  };

  return (
    <Link
      data-track="feed_player_milestone_card"
      to={`/players/${payload.playerId}`}
      className="block bg-surface rounded-lg shadow-sm border border-edge hover:shadow-md transition-shadow duration-200"
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0 bg-amber-100 rounded-full p-2">
              <TrophyIcon className="h-5 w-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-heading">Play Milestone!</p>
              <p className="text-xs text-muted">{formatRelativeTime(createdAt)}</p>
            </div>
          </div>
          {payload.playerAvatar ? (
            <img
              src={payload.playerAvatar}
              alt={payload.playerName}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <div className="h-12 w-12 rounded-full bg-surface-skeleton flex items-center justify-center text-lg font-medium text-body">
              {payload.playerName.charAt(0).toUpperCase()}
            </div>
          )}
        </div>
        <div className="mt-3">
          <p className="text-sm text-body">
            <span className="font-medium text-amber-600">{payload.playerName}</span>
            {' has reached '}
            <span className="font-bold text-amber-600">{getMilestoneEmoji(payload.milestone)} plays</span>
            {'! Keep it up!'}
          </p>
        </div>
      </div>
    </Link>
  );
};
